import {
  cloneDeep, assign, map,
  find, filter, isEqual,
  reduce, forEach, omit,
  split, includes, has,
  get, trim, sortBy,
  uniq, some,
} from 'lodash';
import { format, zonedTimeToUtc } from 'date-fns-tz';
import eventHelpers from './event';
import marketMapper from './market-mapper';
import sportIds from '@/services/helpers/sports';
import { getParamVariantValue, hasParamVariant } from './param-variant-markets';

const {
  BASKETBALL_ID,
  FOOTBALL_ID,
  HOCKEY_ID,
  BASEBALL_ID,
  SOCCER_ID,
} = sportIds;

export const PLAYER_PROPS_LOWER_LIMIT = 0;
export const PLAYER_PROPS_UPPER_LIMIT = 1;
const PLAYER_FIRST_SCORER_MARKET_CODE = 'PLAYER_FIRST_SCORER';

const UP_LOW_CUSTOM_VALUES = {
  rushLoad: {
    lowerLimit: 0,
    upperLimit: 1.5,
  },
  qbLoad: {
    lowerLimit: 0,
    upperLimit: 1.5,
  },
  timeQ1: {
    lowerLimit: 0,
    upperLimit: 12,
  },
  timeQ2: {
    lowerLimit: 0,
    upperLimit: 12,
  },
  timeQ3: {
    lowerLimit: 0,
    upperLimit: 12,
  },
  timeQ4: {
    lowerLimit: 0,
    upperLimit: 12,
  },
  minutesUncertainty: {
    lowerLimit: 0,
    upperLimit: 48,
  },
  playerPositionIndex: {
    lowerLimit: 1,
    upperLimit: 999,
  },
  hitterParams: {
    lineupNumber: {
      lowerLimit: 1,
      upperLimit: 50,
    },
    runAverage: {
      lowerLimit: 0,
      upperLimit: 100,
    },
    runSupport: {
      lowerLimit: 0,
      upperLimit: 100,
    },
  },
  pitcherParams: {
    reliefPitcherOrder: {
      lowerLimit: 1,
      upperLimit: 50,
    },
    maxPitchCount: {
      lowerLimit: 0,
      upperLimit: 500,
    },
  },
  quarterbackParams: {
    passingRunningRatio: {
      lowerLimit: 0.37,
      upperLimit: 0.67,
    },
    passingTouchdownFactor: {
      lowerLimit: -1,
      upperLimit: 1,
    },
    passingCompletionFactor: {
      lowerLimit: -1,
      upperLimit: 1,
    },
    passingYardsFactor: {
      lowerLimit: 0,
      upperLimit: 2,
    },
  },
  lineupLine: {
    lowerLimit: 0,
    upperLimit: 5,
  },
  blocks: {
    lowerLimit: 0,
    upperLimit: 2,
  },
  steals: {
    lowerLimit: 0,
    upperLimit: 2,
  },
  totalPasses: {
    lowerLimit: 0,
    upperLimit: 0.5,
  },
  projections: {
    park: {
      walkProb: {
        lowerLimit: -1,
        upperLimit: 1,
      },
      strikeOutProb: {
        lowerLimit: -1,
        upperLimit: 1,
      },
      singleProb: {
        lowerLimit: -1,
        upperLimit: 1,
      },
      doubleOrTripleProb: {
        lowerLimit: -1,
        upperLimit: 1,
      },
      homeRunProb: {
        lowerLimit: -1,
        upperLimit: 1,
      },
    },
  },
  penaltyConversionRateHome: {
    lowerLimit: 0.25,
    upperLimit: 0.95,
  },
  penaltyConversionRateAway: {
    lowerLimit: 0.25,
    upperLimit: 0.95,
  },
};
const PP_INCREASE_DECREASE_DEFAULT = 0.01;
const PP_INCREASE_DECREASE_VALUES = {
  // BASKETBALL
  threes: 0.005,
  rebounds: 0.005,
  timeQ1: 0.5,
  timeQ2: 0.5,
  timeQ3: 0.5,
  timeQ4: 0.5,
  'quarter4UsageBoost.offensiveReboundPutbackBoost': 0.005,
  steals: 0.005,
  blocks: 0.005,
  assists: 0.005,
  points: 0.005,

  // AF
  tackle: 0.005,
};

export const formatPlayerName = (playerName) => {
  const splittedName = split(playerName, ', ');
  if (splittedName.length < 2) return playerName;

  return `${splittedName[1]} ${splittedName[0]}`;
};

export const formatPlayerFirstName = (playerName) => {
  // eslint-disable-next-line no-unused-vars
  const [lastName, firstName] = split(playerName, ',');
  return trim(firstName);
};

export const formatPlayerLastName = (playerName) => {
  const [lastName] = split(playerName, ',');
  return trim(lastName);
};

export const mapPlayerSetupData = (event) => {
  const eventDetails = eventHelpers.findEventDetails(event);
  const eventData = {
    ...(eventDetails || {}),
    eventId: event.eventId || '',
    eventName: event.eventName || '',
    startsAt: event.startsAt || '',
    isUSAView: event.isUsaView || false,
    matchState: event.matchState || 'N_A',
    sportId: event.sportId,
    sport: event.sport && {
      id: event?.sport?.sportId,
      name: event?.sport?.sportName,
      label: event?.sport?.sportLabel,
    },
    competitionType: eventDetails?.competitionType || '',
    period: eventDetails?.period || 'N_A',
    isSuspended: !!event?.operatorEventSuspensionsByEventId?.nodes?.length,
    isPricesApproved: !!event?.eventReadiness?.isApproved,
    probabilities: event.pricingProbabilitiesByEventId?.nodes[0]?.probabilities || {},
    limbo: event?.limbo,
    isCompetitionMapped: event?.competitionForEventIsMappedsByEventId?.edges[0]?.node?.isMapped || false,
    isDefaultConfigurationGame: event?.defaultBookieWeightsAreUsedForEventsByEventId?.edges[0]?.node?.isDefaultConfigurationGame ?? false,
    isDefaultConfigurationPlayerProps: event?.defaultBookieWeightsAreUsedForEventsByEventId?.edges[0]?.node?.isDefaultConfigurationPlayerProps ?? false,
    mappings: event?.mappings?.nodes,
    bookedEvents: event?.bookedEvents,
    bookedSameGameParlays: event?.bookedSameGameParlays,
    bookedPlayerProps: event?.bookedPlayerProps,
    bookedMicroMarkets: event?.bookedMicroMarkets,
    competition: event?.competition,
  };
  let probabilities = {};
  if (event.pricingProbabilitiesByEventId?.nodes?.length) {
    probabilities = event.pricingProbabilitiesByEventId?.nodes[0]?.probabilities || {};
  }
  const teamA = () => {
    const label = eventData.isUSAView ? 'Away' : 'Home';
    const score = eventData.isUSAView ? eventData.awayScore : eventData.homeScore;
    const team = eventData.isUSAView ? event.competitors?.[1] : event.competitors?.[0];
    const lineup = eventData.isUSAView ? eventData.awayLineup : eventData.homeLineup;
    const teamProbabilites = eventData.isUSAView ? probabilities.awayProbabilities : probabilities.homeProbabilities;
    const possesionOutcome = eventData.isUSAView ? probabilities.awayTeamPossessionOutcome : probabilities.homeTeamPossessionOutcome;
    // For baseball
    const teamABatterIds = eventData?.teamABatterIds;
    const teamAPitcherIds = eventData?.teamAPitcherIds;
    return {
      ...team,
      label,
      score,
      lineup,
      teamABatterIds,
      teamAPitcherIds,
      probabilities: teamProbabilites,
      possesionOutcome,
    };
  };
  const teamB = () => {
    const label = eventData.isUSAView ? 'Home' : 'Away';
    const score = eventData.isUSAView ? eventData.homeScore : eventData.awayScore;
    const team = eventData.isUSAView ? event.competitors?.[0] : event.competitors?.[1];
    const lineup = eventData.isUSAView ? eventData.homeLineup : eventData.awayLineup;
    const teamProbabilites = eventData.isUSAView ? probabilities.homeProbabilities : probabilities.awayProbabilities;
    const possesionOutcome = eventData.isUSAView ? probabilities.homeTeamPossessionOutcome : probabilities.awayTeamPossessionOutcome;
    // For baseball
    const teamBBatterIds = eventData?.teamBBatterIds;
    const teamBPitcherIds = eventData?.teamBPitcherIds;
    return {
      ...team,
      label,
      score,
      lineup,
      teamBBatterIds,
      teamBPitcherIds,
      probabilities: teamProbabilites,
      possesionOutcome,
    };
  };
  return {
    ...eventData,
    teamA: teamA(),
    teamB: teamB(),
  };
};

export const mapUpdatedPlayerSetupData = (data, payload) => {
  const updatedPlayerSetupData = cloneDeep(data);
  const { teamA } = updatedPlayerSetupData;
  const { teamB } = updatedPlayerSetupData;

  teamA.score = updatedPlayerSetupData.isUSAView ? payload.state.awayScore : payload.state.homeScore;
  teamB.score = updatedPlayerSetupData.isUSAView ? payload.state.homeScore : payload.state.awayScore;
  teamA.lineup = updatedPlayerSetupData.isUSAView ? payload.competitors.awayTeam.lineup : payload.competitors.homeTeam.lineup;
  teamB.lineup = updatedPlayerSetupData.isUSAView ? payload.competitors.homeTeam.lineup : payload.competitors.awayTeam.lineup;

  assign(
    updatedPlayerSetupData,
    {
      ...updatedPlayerSetupData,
      teamA,
      teamB,
      period: payload.state.currentClock?.period ?? payload.state?.period,
      secondsLeftInPeriod: payload.state.currentClock?.secondsLeftInPeriod,
      gameTimeElapsedInPeriod: payload.state?.gameTimeElapsedInPeriod,
      teamInPossession: payload.state.teamInPossession ?? payload.state?.inPossession,
      lastTeamToScore: payload.state.lastTeamToScore,
    },
  );

  return updatedPlayerSetupData;
};

const getPlayerSetupNumberValue = (val) => {
  if (typeof val === 'number') return val;
  return val || 0;
};

export const footballPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, disabled, defensivePlayerParams,
    isKicker, isOffensiveNonQB, isQuarterback,
    nonQuarterbackParams, quarterbackParams,
    playerIndex, marketCodeDetails, side, playerPositionIndex,
    isDefensive,
  } = data;
  const playerMarketsLength = marketCodeDetails?.length;
  const suspendedMarketsLength = filter(marketCodeDetails, (market) => market.suspended)?.length || 0;
  const lockedMarketsLength = filter(marketCodeDetails, (market) => market.isLocked)?.length || 0;
  const voidedMarketsLength = filter(marketCodeDetails, (market) => market.voided)?.length || 0;
  const excludedMarketsLength = filter(marketCodeDetails, (market) => market.excluded)?.length || 0;
  const marketsByStatusLength = suspendedMarketsLength + lockedMarketsLength + voidedMarketsLength + excludedMarketsLength;
  const isSuspended = playerMarketsLength && marketsByStatusLength === playerMarketsLength;

  const playerParams = {
    playerParams: {
      playerId,
      playerIndex,
      sport: 'football',
      side,
      isKicker: isKicker || false,
      isOffensiveNonQB: isOffensiveNonQB || false,
      isQuarterback: isQuarterback || false,
      isDefensive: isDefensive || false,
      defensivePlayerParams: {
        tackle: getPlayerSetupNumberValue(defensivePlayerParams?.tackle),
        sack: getPlayerSetupNumberValue(defensivePlayerParams?.sack),
        interception: getPlayerSetupNumberValue(defensivePlayerParams?.interception),
      },
      nonQuarterbackParams: {
        receiveLongNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveLongNoScore),
        receiveLongScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveLongScore),
        receiveShortNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveShortNoScore),
        receiveShortScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveShortScore),
        runLongNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runLongNoScore),
        runLongScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runLongScore),
        runShortNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runShortNoScore),
        runShortScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runShortScore),
      },
      quarterbackParams: {
        interceptionTurnoverRatio: getPlayerSetupNumberValue(quarterbackParams?.interceptionTurnoverRatio),
        passingCompletionFactor: getPlayerSetupNumberValue(quarterbackParams?.passingCompletionFactor),
        passingRunningRatio: getPlayerSetupNumberValue(quarterbackParams?.passingRunningRatio),
        passingTouchdownFactor: getPlayerSetupNumberValue(quarterbackParams?.passingTouchdownFactor),
        passingYardsFactor: getPlayerSetupNumberValue(quarterbackParams?.passingYardsFactor),
        runLongNoScore: getPlayerSetupNumberValue(quarterbackParams?.runLongNoScore),
        runLongScore: getPlayerSetupNumberValue(quarterbackParams?.runLongScore),
        runShortNoScore: getPlayerSetupNumberValue(quarterbackParams?.runShortNoScore),
        runShortScore: getPlayerSetupNumberValue(quarterbackParams?.runShortScore),
      },
    },
    lineup: lineup || false,
    marketCodeDetails: marketCodeDetails || [],
    isSuspended,
    playerPositionIndex,
    disabled: disabled || false,
  };

  if (isQuarterback) {
    playerParams.playerParams.isKicker = false;
    playerParams.playerParams.isOffensiveNonQB = false;
    playerParams.playerParams.isDefensive = false;
    if (has(playerParams.playerParams, 'nonQuarterbackParams')) {
      playerParams.playerParams.nonQuarterbackParams = null;
    }
    if (has(playerParams.playerParams, 'defensivePlayerParams')) {
      playerParams.playerParams.defensivePlayerParams = null;
    }
  }
  if (isOffensiveNonQB) {
    playerParams.playerParams.isKicker = false;
    playerParams.playerParams.isQuarterback = false;
    playerParams.playerParams.isDefensive = false;
    if (has(playerParams.playerParams, 'nonQuarterbackParams')) {
      playerParams.playerParams.quarterbackParams = null;
    }
    if (has(playerParams.playerParams, 'defensivePlayerParams')) {
      playerParams.playerParams.defensivePlayerParams = null;
    }
  }
  if (isKicker) {
    playerParams.playerParams.isOffensiveNonQB = false;
    playerParams.playerParams.isQuarterback = false;
    playerParams.playerParams.isDefensive = false;
    if (has(playerParams.playerParams, 'nonQuarterbackParams')) {
      playerParams.playerParams.nonQuarterbackParams = null;
    }
    if (has(playerParams.playerParams, 'nonQuarterbackParams')) {
      playerParams.playerParams.quarterbackParams = null;
    }
    if (has(playerParams.playerParams, 'defensivePlayerParams')) {
      playerParams.playerParams.defensivePlayerParams = null;
    }
  }

  if (isDefensive) {
    playerParams.playerParams.isOffensiveNonQB = false;
    playerParams.playerParams.isQuarterback = false;
    playerParams.playerParams.isKicker = false;
    if (has(playerParams.playerParams, 'nonQuarterbackParams')) {
      playerParams.playerParams.nonQuarterbackParams = null;
    }
    if (has(playerParams.playerParams, 'quarterbackParams')) {
      playerParams.playerParams.quarterbackParams = null;
    }
  }

  return playerParams;
};

export const priceOnDemandFootballPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, disabled, defensivePlayerParams,
    isKicker, isOffensiveNonQB, isQuarterback,
    nonQuarterbackParams, quarterbackParams,
    playerIndex, marketCodeDetails, side,
    isDefensive,
  } = data;
  const playerParams = {
    playerId,
    playerIndex,
    sport: 'football',
    side,
    marketCodes: map(marketCodeDetails, (market) => market.marketCode),
    disabled: disabled || false,
    lineup: lineup || false,
    isKicker: isKicker || false,
    isOffensiveNonQB: isOffensiveNonQB || false,
    isQuarterback: isQuarterback || false,
    defensivePlayerParams: {
      tackle: getPlayerSetupNumberValue(defensivePlayerParams?.tackle),
      sack: getPlayerSetupNumberValue(defensivePlayerParams?.sack),
      interception: getPlayerSetupNumberValue(defensivePlayerParams?.interception),
    },
    isDefensive: isDefensive || false,
    nonQuarterbackParams: {
      receiveLongNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveLongNoScore),
      receiveLongScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveLongScore),
      receiveShortNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveShortNoScore),
      receiveShortScore: getPlayerSetupNumberValue(nonQuarterbackParams?.receiveShortScore),
      runLongNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runLongNoScore),
      runLongScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runLongScore),
      runShortNoScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runShortNoScore),
      runShortScore: getPlayerSetupNumberValue(nonQuarterbackParams?.runShortScore),
    },
    quarterbackParams: {
      interceptionTurnoverRatio: getPlayerSetupNumberValue(quarterbackParams?.interceptionTurnoverRatio),
      passingCompletionFactor: getPlayerSetupNumberValue(quarterbackParams?.passingCompletionFactor),
      passingRunningRatio: getPlayerSetupNumberValue(quarterbackParams?.passingRunningRatio),
      passingTouchdownFactor: getPlayerSetupNumberValue(quarterbackParams?.passingTouchdownFactor),
      passingYardsFactor: getPlayerSetupNumberValue(quarterbackParams?.passingYardsFactor),
      runLongNoScore: getPlayerSetupNumberValue(quarterbackParams?.runLongNoScore),
      runLongScore: getPlayerSetupNumberValue(quarterbackParams?.runLongScore),
      runShortNoScore: getPlayerSetupNumberValue(quarterbackParams?.runShortNoScore),
      runShortScore: getPlayerSetupNumberValue(quarterbackParams?.runShortScore),
    },
  };
  if (isQuarterback) {
    playerParams.isKicker = false;
    playerParams.isOffensiveNonQB = false;
    playerParams.isDefensive = false;
    if (has(playerParams, 'nonQuarterbackParams')) {
      playerParams.nonQuarterbackParams = null;
    }
    if (has(playerParams, 'defensivePlayerParams')) {
      playerParams.defensivePlayerParams = null;
    }
  }
  if (isOffensiveNonQB) {
    playerParams.isQuarterback = false;
    playerParams.isKicker = false;
    playerParams.isDefensive = false;
    if (has(playerParams, 'nonQuarterbackParams')) {
      playerParams.quarterbackParams = null;
    }
    if (has(playerParams, 'defensivePlayerParams')) {
      playerParams.defensivePlayerParams = null;
    }
  }
  if (isKicker) {
    playerParams.isQuarterback = false;
    playerParams.isOffensiveNonQB = false;
    playerParams.isDefensive = false;
    if (has(playerParams, 'nonQuarterbackParams')) {
      playerParams.nonQuarterbackParams = null;
    }
    if (has(playerParams, 'nonQuarterbackParams')) {
      playerParams.quarterbackParams = null;
    }
    if (has(playerParams, 'defensivePlayerParams')) {
      playerParams.defensivePlayerParams = null;
    }
  }

  if (isDefensive) {
    playerParams.isQuarterback = false;
    playerParams.isOffensiveNonQB = false;
    playerParams.isKicker = false;
    if (has(playerParams, 'nonQuarterbackParams')) {
      playerParams.nonQuarterbackParams = null;
    }
    if (has(playerParams, 'quarterbackParams')) {
      playerParams.quarterbackParams = null;
    }
  }

  return playerParams;
};

export const basketballPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, isStarter, time,
    timeQ1, timeQ2, timeQ3, timeQ4,
    rebounds, assists, points,
    threes, playerIndex, marketCodeDetails, side, playerPositionIndex,
    disabled, quarter4UsageBoost, steals, blocks, paramSource,
    minutesUncertainty,
  } = data;
  const playerMarketsLength = marketCodeDetails?.length;
  const suspendedMarketsLength = filter(marketCodeDetails, (market) => market.suspended)?.length || 0;
  const lockedMarketsLength = filter(marketCodeDetails, (market) => market.isLocked)?.length || 0;
  const voidedMarketsLength = filter(marketCodeDetails, (market) => market.voided)?.length || 0;
  const excludedMarketsLength = filter(marketCodeDetails, (market) => market.excluded)?.length || 0;
  const marketsByStatusLength = suspendedMarketsLength + lockedMarketsLength + voidedMarketsLength + excludedMarketsLength;
  const isSuspended = playerMarketsLength && marketsByStatusLength === playerMarketsLength;
  const playerParams = {
    playerParams: {
      playerId,
      playerIndex,
      isStarter: isStarter || false,
      time: getPlayerSetupNumberValue(time),
      timeQ1: getPlayerSetupNumberValue(timeQ1),
      timeQ2: getPlayerSetupNumberValue(timeQ2),
      timeQ3: getPlayerSetupNumberValue(timeQ3),
      timeQ4: getPlayerSetupNumberValue(timeQ4),
      rebounds: getPlayerSetupNumberValue(rebounds),
      assists: getPlayerSetupNumberValue(assists),
      points: getPlayerSetupNumberValue(points),
      threes: getPlayerSetupNumberValue(threes),
      quarter4UsageBoost: {
        ...quarter4UsageBoost,
        offensiveReboundPutbackBoost: getPlayerSetupNumberValue(quarter4UsageBoost?.offensiveReboundPutbackBoost),
      },
      steals: getPlayerSetupNumberValue(steals),
      blocks: getPlayerSetupNumberValue(blocks),
      minutesUncertainty: getPlayerSetupNumberValue(minutesUncertainty),
      sport: 'basketball',
      side,
    },
    lineup: lineup || false,
    disabled: disabled || false,
    marketCodeDetails: marketCodeDetails || [],
    isSuspended,
    playerPositionIndex,
    paramSource,
  };

  return playerParams;
};

export const priceOnDemandBasketballPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, isStarter, time,
    timeQ1, timeQ2, timeQ3, timeQ4,
    rebounds, assists, points,
    threes, playerIndex, marketCodeDetails,
    side, sport, disabled, quarter4UsageBoost,
    steals, blocks,
    minutesUncertainty,
  } = data;
  const playerParams = {
    playerId,
    playerIndex,
    isStarter: isStarter || false,
    time: getPlayerSetupNumberValue(time),
    timeQ1: getPlayerSetupNumberValue(timeQ1),
    timeQ2: getPlayerSetupNumberValue(timeQ2),
    timeQ3: getPlayerSetupNumberValue(timeQ3),
    timeQ4: getPlayerSetupNumberValue(timeQ4),
    rebounds: getPlayerSetupNumberValue(rebounds),
    assists: getPlayerSetupNumberValue(assists),
    points: getPlayerSetupNumberValue(points),
    threes: getPlayerSetupNumberValue(threes),
    quarter4UsageBoost: {
      ...quarter4UsageBoost,
      offensiveReboundPutbackBoost: getPlayerSetupNumberValue(quarter4UsageBoost?.offensiveReboundPutbackBoost),
    },
    steals: getPlayerSetupNumberValue(steals),
    blocks: getPlayerSetupNumberValue(blocks),
    minutesUncertainty: getPlayerSetupNumberValue(minutesUncertainty),
    lineup: lineup || false,
    disabled: disabled || false,
    side,
    sport,
    marketCodes: map(
      filter(
        marketCodeDetails,
        ({ marketCode }) => isStarter || marketCode !== PLAYER_FIRST_SCORER_MARKET_CODE,
      ),
      (market) => market.marketCode,
    ),
  };

  return playerParams;
};

export const hockeyPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, goals, assists,
    playerIndex, marketCodeDetails, star, side,
    playerPositionIndex, disabled, lineupLine, paramSource,
    saves, shotsOnGoal,
    powerPlayAdjustment,
  } = data;
  const playerMarketsLength = marketCodeDetails?.length;
  const suspendedMarketsLength = filter(marketCodeDetails, (market) => market.suspended)?.length || 0;
  const lockedMarketsLength = filter(marketCodeDetails, (market) => market.isLocked)?.length || 0;
  const voidedMarketsLength = filter(marketCodeDetails, (market) => market.voided)?.length || 0;
  const excludedMarketsLength = filter(marketCodeDetails, (market) => market.excluded)?.length || 0;
  const marketsByStatusLength = suspendedMarketsLength + lockedMarketsLength + voidedMarketsLength + excludedMarketsLength;
  const isSuspended = playerMarketsLength && marketsByStatusLength === playerMarketsLength;
  const playerParams = {
    playerParams: {
      playerId,
      playerIndex,
      star: star || false,
      goals: getPlayerSetupNumberValue(goals),
      assists: getPlayerSetupNumberValue(assists),
      lineupLine: getPlayerSetupNumberValue(lineupLine),
      powerPlayAdjustment: getPlayerSetupNumberValue(powerPlayAdjustment),
      saves: getPlayerSetupNumberValue(saves),
      shotsOnGoal: getPlayerSetupNumberValue(shotsOnGoal),
      sport: 'hockey',
      side,
    },
    lineup: lineup || false,
    disabled: disabled || false,
    marketCodeDetails: marketCodeDetails || [],
    isSuspended,
    playerPositionIndex,
    paramSource,
  };

  return playerParams;
};

export const priceOnDemandHockeyPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, goals, assists,
    playerIndex, marketCodeDetails, star, side,
    disabled, lineupLine, saves, shotsOnGoal,
    powerPlayAdjustment,
  } = data;
  const playerParams = {
    playerId,
    playerIndex,
    star: star || false,
    goals: getPlayerSetupNumberValue(goals),
    assists: getPlayerSetupNumberValue(assists),
    lineupLine: getPlayerSetupNumberValue(lineupLine),
    powerPlayAdjustment: getPlayerSetupNumberValue(powerPlayAdjustment),
    shotsOnGoal: getPlayerSetupNumberValue(shotsOnGoal),
    saves: getPlayerSetupNumberValue(saves),
    sport: 'hockey',
    side,
    lineup: lineup || false,
    disabled: disabled || false,
    marketCodes: map(marketCodeDetails, (market) => market.marketCode),
  };
  return playerParams;
};

export const baseballPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup,
    playerIndex, marketCodeDetails, side,
    hitterParams, pitcherParams, isPitcher, isHitter,
    playerPositionIndex, disabled,
  } = data;
  const playerMarketsLength = marketCodeDetails?.length;
  const suspendedMarketsLength = filter(marketCodeDetails, (market) => market.suspended)?.length || 0;
  const lockedMarketsLength = filter(marketCodeDetails, (market) => market.isLocked)?.length || 0;
  const voidedMarketsLength = filter(marketCodeDetails, (market) => market.voided)?.length || 0;
  const excludedMarketsLength = filter(marketCodeDetails, (market) => market.excluded)?.length || 0;
  const marketsByStatusLength = suspendedMarketsLength + lockedMarketsLength + voidedMarketsLength + excludedMarketsLength;
  const isSuspended = playerMarketsLength && marketsByStatusLength === playerMarketsLength;
  const playerParams = {
    playerParams: {
      playerId,
      playerIndex,
      sport: 'baseball',
      side,
      hitterParams: {
        buntProb: getPlayerSetupNumberValue(hitterParams?.buntProb),
        doublePlayProb: getPlayerSetupNumberValue(hitterParams?.doublePlayProb),
        isCatcher: hitterParams?.isCatcher || false,
        lineupNumber: getPlayerSetupNumberValue(hitterParams?.lineupNumber),
        runAverage: getPlayerSetupNumberValue(hitterParams?.runAverage),
        runSupport: getPlayerSetupNumberValue(hitterParams?.runSupport),
        handedness: hitterParams?.handedness || 'RIGHT',
        againstLeftHandedEff: getPlayerSetupNumberValue(hitterParams?.againstLeftHandedEff),
        againstRightHandedEff: getPlayerSetupNumberValue(hitterParams?.againstRightHandedEff),
        pinchHitRisk: getPlayerSetupNumberValue(hitterParams?.pinchHitRisk),
        leftHandedProjection: {
          walkProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.walkProb),
          strikeOutProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.strikeOutProb),
          singleProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.singleProb),
          doubleOrTripleProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.doubleOrTripleProb),
          homeRunProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.homeRunProb),
        },
        rightHandedProjection: {
          walkProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.walkProb),
          strikeOutProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.strikeOutProb),
          singleProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.singleProb),
          doubleOrTripleProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.doubleOrTripleProb),
          homeRunProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.homeRunProb),
        },
      },
      pitcherParams: {
        reliefPitcherOrder: getPlayerSetupNumberValue(pitcherParams?.reliefPitcherOrder),
        maxPitchCount: getPlayerSetupNumberValue(pitcherParams?.maxPitchCount),
        isNextPitcher: pitcherParams?.isNextPitcher || false,
        isCurrentPitcher: pitcherParams?.isCurrentPitcher || false,
        hittersSeen: pitcherParams?.hittersSeen || [],
        handedness: pitcherParams?.handedness || 'RIGHT',
        catcherProjection: {
          leftHandedProjection: {
            walkProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.walkProb),
            strikeOutProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.strikeOutProb),
            singleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.singleProb),
            doubleOrTripleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.doubleOrTripleProb),
            homeRunProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.homeRunProb),
          },
          rightHandedProjection: {
            walkProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.walkProb),
            strikeOutProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.strikeOutProb),
            singleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.singleProb),
            doubleOrTripleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.doubleOrTripleProb),
            homeRunProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.homeRunProb),
          },
          pitchesPerBatToLefties: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.pitchesPerBatToLefties),
          pitchesPerBatToRighties: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.pitchesPerBatToRighties),
          againstLeftHandedEff: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.againstLeftHandedEff),
          againstRightHandedEff: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.againstRightHandedEff),
          doublePlayProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.doublePlayProb),
        },
      },
    },
    isPitcher,
    isHitter,
    lineup: lineup || false,
    marketCodeDetails: marketCodeDetails || [],
    isSuspended,
    playerPositionIndex,
    disabled: disabled || false,
  };

  if (isPitcher && !isHitter) {
    playerParams.playerParams.hitterParams = null;
  }
  if (isHitter && !isPitcher) {
    playerParams.playerParams.pitcherParams = null;
  }

  return playerParams;
};

export const priceOnDemandBaseballPlayerParamsSetup = (data = {}) => {
  const {
    playerId, marketCodeDetails,
    playerIndex, side,
    hitterParams, pitcherParams,
    isPitcher, isHitter, disabled,
    lineup,
  } = data;
  const playerParams = {
    playerId,
    playerIndex,
    sport: 'baseball',
    side,
    marketCodes: map(marketCodeDetails, (market) => market.marketCode),
    disabled: disabled || false,
    lineup: lineup || false,
    hitterParams: {
      buntProb: getPlayerSetupNumberValue(hitterParams?.buntProb),
      doublePlayProb: getPlayerSetupNumberValue(hitterParams?.doublePlayProb),
      isCatcher: hitterParams?.isCatcher || false,
      lineupNumber: getPlayerSetupNumberValue(hitterParams?.lineupNumber),
      runAverage: getPlayerSetupNumberValue(hitterParams?.runAverage),
      runSupport: getPlayerSetupNumberValue(hitterParams?.runSupport),
      handedness: hitterParams?.handedness || 'RIGHT',
      pinchHitRisk: getPlayerSetupNumberValue(hitterParams?.pinchHitRisk),
      leftHandedProjection: {
        walkProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.walkProb),
        strikeOutProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.strikeOutProb),
        singleProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.singleProb),
        doubleOrTripleProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.doubleOrTripleProb),
        homeRunProb: getPlayerSetupNumberValue(hitterParams?.leftHandedProjection?.homeRunProb),
      },
      rightHandedProjection: {
        walkProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.walkProb),
        strikeOutProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.strikeOutProb),
        singleProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.singleProb),
        doubleOrTripleProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.doubleOrTripleProb),
        homeRunProb: getPlayerSetupNumberValue(hitterParams?.rightHandedProjection?.homeRunProb),
      },
    },
    pitcherParams: {
      reliefPitcherOrder: getPlayerSetupNumberValue(pitcherParams?.reliefPitcherOrder),
      maxPitchCount: getPlayerSetupNumberValue(pitcherParams?.maxPitchCount),
      isNextPitcher: pitcherParams?.isNextPitcher || false,
      isCurrentPitcher: pitcherParams?.isCurrentPitcher || false,
      hittersSeen: pitcherParams?.hittersSeen || [],
      handedness: pitcherParams?.handedness || 'RIGHT',
      catcherProjection: {
        leftHandedProjection: {
          walkProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.walkProb),
          strikeOutProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.strikeOutProb),
          singleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.singleProb),
          doubleOrTripleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.doubleOrTripleProb),
          homeRunProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.leftHandedProjection?.homeRunProb),
        },
        rightHandedProjection: {
          walkProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.walkProb),
          strikeOutProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.strikeOutProb),
          singleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.singleProb),
          doubleOrTripleProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.doubleOrTripleProb),
          homeRunProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.rightHandedProjection?.homeRunProb),
        },
        pitchesPerBatToLefties: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.pitchesPerBatToLefties),
        pitchesPerBatToRighties: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.pitchesPerBatToRighties),
        doublePlayProb: getPlayerSetupNumberValue(pitcherParams?.catcherProjection?.doublePlayProb),
      },
    },
  };

  if (isPitcher && !isHitter) {
    playerParams.hitterParams = null;
  }
  if (isHitter && !isPitcher) {
    playerParams.pitcherParams = null;
  }

  return playerParams;
};

export const soccerPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, goals, minutes,
    playerIndex, marketCodeDetails, penalty, side, assists, shotsOnTarget,
    playerPositionIndex, disabled, yellowCards, redCards, paramSource,
    totalShots, totalPasses, accuratePasses, tackles, interceptions, fouls, saves, offsides,
    accurateCrosses, totalCrosses,
  } = data;
  const playerMarketsLength = marketCodeDetails?.length;
  const suspendedMarketsLength = filter(marketCodeDetails, (market) => market.suspended)?.length || 0;
  const lockedMarketsLength = filter(marketCodeDetails, (market) => market.isLocked)?.length || 0;
  const voidedMarketsLength = filter(marketCodeDetails, (market) => market.voided)?.length || 0;
  const excludedMarketsLength = filter(marketCodeDetails, (market) => market.excluded)?.length || 0;
  const marketsByStatusLength = suspendedMarketsLength + lockedMarketsLength + voidedMarketsLength + excludedMarketsLength;
  const isSuspended = playerMarketsLength && marketsByStatusLength === playerMarketsLength;
  const playerParams = {
    playerParams: {
      playerId,
      playerIndex,
      goals: getPlayerSetupNumberValue(goals),
      minutes: getPlayerSetupNumberValue(minutes),
      penalty: getPlayerSetupNumberValue(penalty),
      yellowCards: getPlayerSetupNumberValue(yellowCards),
      redCards: getPlayerSetupNumberValue(redCards),
      assists: getPlayerSetupNumberValue(assists),
      shotsOnTarget: getPlayerSetupNumberValue(shotsOnTarget),
      totalShots: getPlayerSetupNumberValue(totalShots),
      totalPasses: getPlayerSetupNumberValue(totalPasses),
      accuratePasses: getPlayerSetupNumberValue(accuratePasses),
      tackles: getPlayerSetupNumberValue(tackles),
      interceptions: getPlayerSetupNumberValue(interceptions),
      offsides: getPlayerSetupNumberValue(offsides),
      fouls: getPlayerSetupNumberValue(fouls),
      saves: getPlayerSetupNumberValue(saves),
      accurateCrosses: getPlayerSetupNumberValue(accurateCrosses),
      totalCrosses: getPlayerSetupNumberValue(totalCrosses),
      sport: 'soccer',
      side,
    },
    lineup: lineup || false,
    disabled: disabled || false,
    marketCodeDetails: marketCodeDetails || [],
    isSuspended,
    playerPositionIndex,
    paramSource,
  };

  return playerParams;
};

export const priceOnDemandSoccerPlayerParamsSetup = (data = {}) => {
  const {
    playerId, lineup, goals,
    playerIndex, marketCodeDetails, star, side,
    disabled, minutes, penalty, yellowCards, redCards,
    assists, shotsOnTarget, totalShots, totalPasses,
    accuratePasses, tackles, interceptions, fouls, saves, offsides,
    accurateCrosses, totalCrosses,
  } = data;
  const playerParams = {
    playerId,
    playerIndex,
    star: star || false,
    goals: getPlayerSetupNumberValue(goals),
    minutes: getPlayerSetupNumberValue(minutes),
    penalty: getPlayerSetupNumberValue(penalty),
    yellowCards: getPlayerSetupNumberValue(yellowCards),
    redCards: getPlayerSetupNumberValue(redCards),
    assists: getPlayerSetupNumberValue(assists),
    shotsOnTarget: getPlayerSetupNumberValue(shotsOnTarget),
    totalShots: getPlayerSetupNumberValue(totalShots),
    totalPasses: getPlayerSetupNumberValue(totalPasses),
    accuratePasses: getPlayerSetupNumberValue(accuratePasses),
    tackles: getPlayerSetupNumberValue(tackles),
    interceptions: getPlayerSetupNumberValue(interceptions),
    offsides: getPlayerSetupNumberValue(offsides),
    fouls: getPlayerSetupNumberValue(fouls),
    saves: getPlayerSetupNumberValue(saves),
    accurateCrosses: getPlayerSetupNumberValue(accurateCrosses),
    totalCrosses: getPlayerSetupNumberValue(totalCrosses),
    sport: 'soccer',
    side,
    lineup: lineup || false,
    disabled: disabled || false,
    marketCodes: map(marketCodeDetails, (market) => market.marketCode),
  };
  return playerParams;
};

export const mapPlayerSetupPayloadData = (playerSetupData, playerSetupTableData, selectedParamSource = 'MANUAL') => {
  const { isUSAView, sportId } = playerSetupData;
  const {
    teamA,
    teamB,
  } = playerSetupTableData;

  const homeTeam = {};
  const awayTeam = {};

  const firstTeam = isUSAView ? teamB : teamA;
  const secondTeam = isUSAView ? teamA : teamB;
  switch (sportId) {
  case FOOTBALL_ID:
    homeTeam.playersParams = map(firstTeam, (playerData) => footballPlayerParamsSetup(playerData));
    awayTeam.playersParams = map(secondTeam, (playerData) => footballPlayerParamsSetup(playerData));
    break;
  case BASKETBALL_ID:
    homeTeam.playersParams = map(firstTeam, (playerData) => basketballPlayerParamsSetup(playerData));
    awayTeam.playersParams = map(secondTeam, (playerData) => basketballPlayerParamsSetup(playerData));
    break;
  case HOCKEY_ID:
    homeTeam.playersParams = map(firstTeam, (playerData) => hockeyPlayerParamsSetup(playerData));
    awayTeam.playersParams = map(secondTeam, (playerData) => hockeyPlayerParamsSetup(playerData));
    break;

  case BASEBALL_ID:
    homeTeam.playersParams = map(firstTeam, (playerData) => baseballPlayerParamsSetup(playerData));
    awayTeam.playersParams = map(secondTeam, (playerData) => baseballPlayerParamsSetup(playerData));
    break;
  case SOCCER_ID:
    homeTeam.playersParams = map(firstTeam, (playerData) => soccerPlayerParamsSetup(playerData));
    awayTeam.playersParams = map(secondTeam, (playerData) => soccerPlayerParamsSetup(playerData));
    break;
  default:
    break;
  }
  const payload = {
    homeTeam,
    awayTeam,
    selectedParamSource: selectedParamSource || 'MANUAL',
  };
  if (playerSetupData?.competitionType === 'NBA' || playerSetupData?.competitionType === 'FIBA' || sportId === SOCCER_ID) {
    payload.playerComboMarketCodeDetails = playerSetupTableData.playerComboMarketCodeDetails || {};
  }
  return payload;
};
const FOOTBALL_PLAYER_REQUIRED_FIELDS = ['nonQuarterbackParams.receiveLongNoScore', 'nonQuarterbackParams.receiveLongScore', 'nonQuarterbackParams.receiveShortNoScore', 'nonQuarterbackParams.receiveShortScore', 'nonQuarterbackParams.runLongNoScore', 'nonQuarterbackParams.runLongScore', 'nonQuarterbackParams.runShortNoScore', 'nonQuarterbackParams.runShortScore', 'quarterbackParams.interceptionTurnoverRatio', 'quarterbackParams.passingCompletionFactor', 'quarterbackParams.passingRunningRatio', 'quarterbackParams.passingTouchdownFactor', 'quarterbackParams.passingYardsFactor', 'quarterbackParams.runLongNoScore', 'quarterbackParams.runLongScore', 'quarterbackParams.runShortNoScore', 'quarterbackParams.runShortScore', 'defensivePlayerParams.tackle', 'defensivePlayerParams.sack', 'defensivePlayerParams.interception'];
const BASKETBALL_PLAYER_REQUIRED_FIELDS = ['timeQ1', 'timeQ2', 'timeQ3', 'timeQ4', 'rebounds', 'assists', 'points', 'threes', 'quarter4UsageBoost.offensiveReboundPutbackBoost', 'steals', 'blocks', 'playerPositionIndex'];
const HOCKEY_PLAYER_REQUIRED_FIELDS = ['goals', 'assists', 'playerPositionIndex'];
const BASEBALL_PLAYER_REQUIRED_FIELDS = ['hitterParams.lineupNumber', 'pitcherParams.maxPitchCount', 'pitcherParams.reliefPitcherOrder'];
const SOCCER_PLAYER_REQUIRED_FIELDS = ['goals', 'minutes', 'penalty', 'yellowCards', 'redCards'];
export const BASEBALL_PROTECTED_MANUAL_PROJECTION_PARAM_PATHS = [
  'pitcherParams.maxPitchCount',
  'hitterParams.isCatcher',
  'pitcherParams.isCurrentPitcher',
  'pitcherParams.isCurrentPitch',
  'pitcherParams.isNextPitcher',
  'pitcherParams.isNextPitch',
  'hitterParams.lineupNumber',
  'pitcherParams.reliefPitcherOrder',
];

export const requiredFieldsBySport = (sportId) => {
  let requiredFields;
  switch (sportId) {
  case FOOTBALL_ID: requiredFields = FOOTBALL_PLAYER_REQUIRED_FIELDS; break;
  case BASKETBALL_ID: requiredFields = BASKETBALL_PLAYER_REQUIRED_FIELDS; break;
  case HOCKEY_ID: requiredFields = HOCKEY_PLAYER_REQUIRED_FIELDS; break;
  case BASEBALL_ID: requiredFields = BASEBALL_PLAYER_REQUIRED_FIELDS; break;
  case SOCCER_ID: requiredFields = SOCCER_PLAYER_REQUIRED_FIELDS; break;
  default: break;
  }
  return requiredFields;
};

export const getLowerLimit = (field) => get(UP_LOW_CUSTOM_VALUES, field)?.lowerLimit || PLAYER_PROPS_LOWER_LIMIT;
export const getUpperLimit = (field) => get(UP_LOW_CUSTOM_VALUES, field)?.upperLimit || PLAYER_PROPS_UPPER_LIMIT;

export const getPlayerPropIncreaseDecreaseValue = (field) => get(PP_INCREASE_DECREASE_VALUES, field) || PP_INCREASE_DECREASE_DEFAULT;

export const checkIfRequiredFieldsEnteredByRow = (requiredFields, player) => {
  let allRequiredFieldsEntered = true;
  forEach(requiredFields, (field) => {
    if (
      (!get(player, field) && get(player, field) !== 0)
      || (
        (get(player, field) < getLowerLimit(field))
        || (get(player, field) > getUpperLimit(field))
      )
      || typeof get(player, field) !== 'number') {
      allRequiredFieldsEntered = false;
      return false;
    }
    return true;
  });
  return allRequiredFieldsEntered;
};

export const requiredFieldsByPlayer = (player, sportId, sportRequiredFields) => {
  let requiredFields;
  if (sportId === BASEBALL_ID) {
    const { isPitcher } = player;
    const { isHitter } = player;
    if (isPitcher && isHitter) {
      requiredFields = sportRequiredFields;
    } else if (isPitcher) {
      requiredFields = filter(sportRequiredFields, (field) => includes(field, 'pitcherParams'));
    } else if (isHitter) {
      requiredFields = filter(sportRequiredFields, (field) => includes(field, 'hitterParams'));
    }
    // Adding playerPositionIndex as required field for both pitcher and hitter.
    requiredFields = [
      ...requiredFields,
      'playerPositionIndex',
    ];
  } else if (sportId === FOOTBALL_ID) {
    const { isQuarterback, isOffensiveNonQB, isDefensive } = player;
    if (isQuarterback) {
      requiredFields = filter(sportRequiredFields, (field) => includes(field, 'quarterbackParams'));
    } else if (isOffensiveNonQB) {
      requiredFields = filter(sportRequiredFields, (field) => includes(field, 'nonQuarterbackParams'));
    } else if (isDefensive) {
      requiredFields = filter(sportRequiredFields, (field) => includes(field, 'defensivePlayerParams'));
    }
    // Adding playerPositionIndex as required field for both quarterback and offensiveNonQB.
    requiredFields = [
      ...(requiredFields || []),
      'playerPositionIndex',
    ];
  } else {
    requiredFields = sportRequiredFields;
  }
  return requiredFields;
};

export const checkIfRequiredFieldsEntered = (data, sportId) => {
  if (!data.length) return true;
  if (sportId === BASEBALL_ID) {
    const numberOfCurrentPitchers = filter(data, (player) => player?.pitcherParams?.isCurrentPitcher).length;
    if (numberOfCurrentPitchers > 1) return false;
  }
  let allRequiredFieldsEntered = true;
  const requiredFields = requiredFieldsBySport(sportId);

  forEach(data, (player) => {
    const requiredFieldsByPlayerAndSport = requiredFieldsByPlayer(player, sportId, requiredFields);
    allRequiredFieldsEntered = checkIfRequiredFieldsEnteredByRow(requiredFieldsByPlayerAndSport, player);
    if (!allRequiredFieldsEntered) {
      return false;
    }
    return true;
  });
  return allRequiredFieldsEntered;
};

const mapPriceOnDemandPayloadByTeam = (teamData, sportId) => {
  let paramsBySportSetupFunction;
  switch (sportId) {
  case FOOTBALL_ID:
    paramsBySportSetupFunction = priceOnDemandFootballPlayerParamsSetup;
    break;
  case BASKETBALL_ID:
    paramsBySportSetupFunction = priceOnDemandBasketballPlayerParamsSetup;
    break;
  case HOCKEY_ID:
    paramsBySportSetupFunction = priceOnDemandHockeyPlayerParamsSetup;
    break;
  case BASEBALL_ID:
    paramsBySportSetupFunction = priceOnDemandBaseballPlayerParamsSetup;
    break;
  case SOCCER_ID:
    paramsBySportSetupFunction = priceOnDemandSoccerPlayerParamsSetup;
    break;
  default:
    break;
  }
  return map(teamData, (playerData) => paramsBySportSetupFunction(playerData));
};

export const mapPriceOnDemandPayload = (playerSetupData, playerSetupTableData) => {
  if (!playerSetupData || !playerSetupTableData) return {};
  const { sportId, competitionType } = playerSetupData;
  const isUSAView = has(playerSetupData, 'isUSAView') ? playerSetupData.isUSAView : playerSetupData.isUsaView;
  const {
    teamA,
    teamB,
  } = playerSetupTableData;

  const homeTeam = {};
  const awayTeam = {};

  // Temp solution for rush percentage
  if (sportId === FOOTBALL_ID) {
    homeTeam.rushPercentage = 0.33;
    awayTeam.rushPercentage = 0.33;
  }

  const firstTeam = isUSAView ? teamB : teamA;
  const secondTeam = isUSAView ? teamA : teamB;
  homeTeam.players = mapPriceOnDemandPayloadByTeam(firstTeam, sportId);
  awayTeam.players = mapPriceOnDemandPayloadByTeam(secondTeam, sportId);

  const payload = {
    homeTeam,
    awayTeam,
  };
  if (competitionType === 'NBA' || competitionType === 'FIBA' || sportId === SOCCER_ID) {
    payload.comboPlayerMarkets = reduce(
      playerSetupTableData.playerComboMarketCodeDetails,
      (allMarkets, currentMarket, key) => {
        const markets = map(currentMarket, ({ marketCode }) => marketCode);
        return {
          ...allMarkets,
          [key]: markets,
        };
      }, {},
    );
  }

  return payload;
};

const mapAdditionalPlayerParams = (params) => ({
  // Baseball params
  isPitcher: params.isPitcher,
  isHitter: params.isHitter,
  // AF params
  isQuarterback: params?.playerParams?.isQuarterback,
  isOffensiveNonQB: params?.playerParams?.isOffensiveNonQB,
  isDefensive: params?.playerParams?.isDefensive,
  playerPositionIndex: params?.playerPositionIndex || 1,
  disabled: params?.disabled,
  isPlayerInCalculatedMode: params?.paramSource === 'CALCULATED',
  calculatedParamsAvailable: params?.calculatedParamsAvailable,
  calculatedParamsUpdatedAt: params?.calculatedParamsUpdatedAt ? format(zonedTimeToUtc(params?.calculatedParamsUpdatedAt, 'UTC'), 'hh:mm') : '',
  paramSource: params?.paramSource,
  paramFindingErrorMessage: params?.paramFindingErrorMessage,

  // Basketball params
  totalMinutes: params?.playerParams?.timeQ1 + params?.playerParams?.timeQ2 + params?.playerParams?.timeQ3 + params?.playerParams?.timeQ4,
  timeQ1Percentage: Number((params?.minutesDistribution?.minutesDistributionQ1 * 100).toFixed(2)) || 0,
  timeQ2Percentage: Number((params?.minutesDistribution?.minutesDistributionQ2 * 100).toFixed(2)) || 0,
  timeQ3Percentage: Number((params?.minutesDistribution?.minutesDistributionQ3 * 100).toFixed(2)) || 0,
  timeQ4Percentage: Number((params?.minutesDistribution?.minutesDistributionQ4 * 100).toFixed(2)) || 0,
});

const mergePlayerWithEventMarkets = (playerMarkets, eventMarkets, playerId) => map(
  playerMarkets,
  (playerMarket) => {
    const marketsByCode = filter(eventMarkets, (market) => market.marketCode === playerMarket.marketCode);
    if (some(marketsByCode, (market) => hasParamVariant(market.marketType?.params))) {
      return {
        ...playerMarket,
        currentMarket: reduce(marketsByCode, (markets, market) => ({
          ...markets,
          [getParamVariantValue(market.marketType?.params)]: market,
        }), {}),
        availableParamVariants: sortBy(reduce(marketsByCode, (variants, market) => {
          const variantValue = getParamVariantValue(market.marketType?.params);
          return variantValue != null ? [...variants, variantValue] : variants;
        }, []), (variant) => variant),
        isParamVariantMarket: true,
        marketCode: playerMarket.marketCode,
      };
    }

    return {
      ...playerMarket,
      currentMarket: find(eventMarkets, { marketCode: playerMarket.marketCode, marketSummary: { playerId } }),
    };
  },
);

export const mapPlayerSetupTableDataResponse = (playerSetupData, playerSetupTableData, squadData, playerMarketsFromEvent) => {
  const { isUSAView, competitionType, sportId } = playerSetupData;
  const { homeTeam, awayTeam } = playerSetupTableData;
  const { teamASquad, teamBSquad } = squadData;
  const homeTeamData = map(homeTeam?.playersParams, (player) => ({
    ...player?.playerParams,
    lineup: player?.lineup,
    voided: player?.voided,
    marketCodeDetails: mergePlayerWithEventMarkets(player?.marketCodeDetails, playerMarketsFromEvent, player?.playerParams?.playerId),
    ...mapAdditionalPlayerParams(player),
  }));
  const awayTeamData = map(awayTeam?.playersParams, (player) => ({
    ...player?.playerParams,
    lineup: player?.lineup,
    voided: player?.voided,
    marketCodeDetails: mergePlayerWithEventMarkets(player?.marketCodeDetails, playerMarketsFromEvent, player?.playerParams?.playerId),
    ...mapAdditionalPlayerParams(player),
  }));
  const data = {
    teamA: isUSAView ? awayTeamData : homeTeamData,
    teamB: isUSAView ? homeTeamData : awayTeamData,
  };

  data.teamA = map(data.teamA, (player) => ({
    ...player,
    playerLocked: true,
    playerName: find(teamASquad, { playerIndex: player.playerIndex })?.personalInfo?.name,
    position: find(teamASquad, { playerIndex: player.playerIndex })?.position || null,
    marketCodeDetails: map(player?.marketCodeDetails, (market) => ({
      ...market,
      marketName: marketMapper.playersMarketName(market?.marketCode, sportId),
    })),
    isSuspended:
    !!player.marketCodeDetails?.length
    && !filter(player.marketCodeDetails, (market) => !market.suspended)?.length,
  }));
  data.teamB = map(data.teamB, (player) => ({
    ...player,
    playerLocked: true,
    playerName: find(teamBSquad, { playerIndex: player.playerIndex })?.personalInfo?.name,
    position: find(teamBSquad, { playerIndex: player.playerIndex })?.position || null,
    marketCodeDetails: map(player?.marketCodeDetails, (market) => ({
      ...market,
      marketName: marketMapper.playersMarketName(market?.marketCode, sportId),
    })),
    isSuspended:
    !!player.marketCodeDetails?.length
    && !filter(player.marketCodeDetails, (market) => !market.suspended)?.length,
  }));

  if (competitionType === 'NBA' || competitionType === 'FIBA' || playerSetupData?.sportId === SOCCER_ID) {
    data.playerComboMarketCodeDetails = reduce(
      playerSetupTableData?.playerComboMarketCodeDetails,
      (allMarkets, currentMarket, key) => {
        // Extract the other player index from the key
        const indices = key.match(/\((-?\d+),\s*(-?\d+)\)/);
        // eslint-disable-next-line no-unused-vars
        const [_, index1, index2] = indices;
        const markets = map(currentMarket, (market) => ({
          ...market,
          playersCombo: {
            first: parseInt(index1, 10),
            second: parseInt(index2, 10),
          },
          isLocked: false,
          isComboMarket: true,
          marketName: marketMapper.comboPlayersMarketName(market.marketCode),
          currentMarket: find(playerMarketsFromEvent, (marketFromEvent) => (
            market.marketCode === marketFromEvent.marketCode
            && marketFromEvent?.marketType?.params?.FIRST_PLAYER_INDEX === parseInt(index1, 10)
            && marketFromEvent?.marketType?.params?.SECOND_PLAYER_INDEX === parseInt(index2, 10)
          )),
        }));
        return {
          ...allMarkets,
          [key]: markets,
        };
      }, {},
    );
    // Check are players suspended based on the combo markets
    data.teamA = map(data.teamA, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(data.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          playerHasComboMarket = true;
          const markets = data.playerComboMarketCodeDetails[key];
          return markets.length && markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
      };
    });
    data.teamB = map(data.teamB, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(data.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          playerHasComboMarket = true;
          const markets = data.playerComboMarketCodeDetails[key];
          return markets.length && markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
      };
    });
  }

  return data;
};

const omitFieldsFromArrayOfObjects = (data, fields) => map(data, (item) => omit(item, fields));

const isPlayersMarketsChanged = (newData, originalData) => {
  const newDataCopy = cloneDeep(newData);
  const originalDataCopy = cloneDeep(originalData);

  let playerMarketsChanged = false;

  forEach(newDataCopy.teamA, (player) => {
    const originalMarketsLength = find(originalDataCopy.teamA, { playerId: player.playerId })?.marketCodeDetails?.length;
    if (player.marketCodeDetails?.length !== originalMarketsLength) playerMarketsChanged = true;
  });

  forEach(newDataCopy.teamB, (player) => {
    const originalMarketsLength = find(originalDataCopy.teamB, { playerId: player.playerId })?.marketCodeDetails?.length;
    if (player.marketCodeDetails?.length !== originalMarketsLength) playerMarketsChanged = true;
  });

  return playerMarketsChanged;
};

export const checkIsPlayerSetupSubmitEnabled = ({
  newData, originalData, sportId,
}) => {
  const newDataCopy = cloneDeep(newData);
  const originalDataCopy = cloneDeep(originalData);
  newDataCopy.teamA = omitFieldsFromArrayOfObjects(newData.teamA, ['marketCodeDetails', 'isSuspended']);
  newDataCopy.teamB = omitFieldsFromArrayOfObjects(newData.teamB, ['marketCodeDetails', 'isSuspended']);
  originalDataCopy.teamA = omitFieldsFromArrayOfObjects(originalDataCopy.teamA, ['marketCodeDetails', 'isSuspended']);
  originalDataCopy.teamB = omitFieldsFromArrayOfObjects(originalDataCopy.teamB, ['marketCodeDetails', 'isSuspended']);

  // If there are no added players submit should be disabled
  if (!newDataCopy.teamA.length && !newDataCopy.teamB.length) {
    return {
      isPlayerParamsChanged: false,
      isPlayerParamsValid: true,
    };
  }

  const requiredFieldsEntered = checkIfRequiredFieldsEntered(newDataCopy.teamA, sportId)
    && checkIfRequiredFieldsEntered(newDataCopy.teamB, sportId);

  return {
    isPlayerParamsChanged: !isEqual(newDataCopy, originalDataCopy)
    || isPlayersMarketsChanged(newData, originalData),
    isPlayerParamsValid: requiredFieldsEntered,
  };
};

export const mapMarketsByPlayerIndex = (players) => reduce(
  players,
  (allPlayers, player) => {
    const playerIndex = player?.playerIndex;
    const marketsPerPlayer = {};
    forEach(filter(player.markets, (market) => (
      market?.marketSummary?.isMainLine
      // Markets without line
      || includes(market?.marketType?.marketCode, 'SCORER')
      || hasParamVariant(market?.marketType?.params)
      // Baseball markets without line
      || includes(['PLAYER_HOME_RUN', 'PLAYER_ON_BASE', 'PLAYER_ON_BASE_TWO_PLUS'], market?.marketType?.marketCode)
      // AF markets without line
      || includes(['PLAYER_TOUCHDOWN_TWO_OR_MORE', 'PLAYER_TOUCHDOWN_THREE_OR_MORE', 'PLAYER_SACKS_YES_NO', 'PLAYER_INTERCEPTIONS_MADE_YES_NO'], market?.marketType?.marketCode)
      // BB markets without line
      || includes([
        'PLAYER_DOUBLE_DOUBLE',
        'PLAYER_TRIPLE_DOUBLE',
        'PLAYER_TOP_POINTS',
        'PLAYER_TOP_REBOUNDS',
        'PLAYER_TOP_ASSISTS',
        'PLAYER_TOP_THREES'], market?.marketType?.marketCode)
      // Soccer markets without line
      || includes([
        'PLAYER_TO_GET_YELLOW_CARD',
        'PLAYER_TO_GET_FIRST_CARD',
        'PLAYER_TO_GET_RED_CARD',
        'PLAYER_TO_SCORE_OR_ASSIST',
        'PLAYER_TO_GET_CARD',
        'PLAYER_ANYTIME_ASSIST',
        'PLAYER_TO_GET_YELLOW_CARD_SUPER_SUB',
        'PLAYER_TO_GET_FIRST_CARD_SUPER_SUB',
        'PLAYER_TO_GET_RED_CARD_SUPER_SUB',
        'PLAYER_TO_SCORE_OR_ASSIST_SUPER_SUB',
        'PLAYER_TO_GET_CARD_SUPER_SUB',
        'PLAYER_ANYTIME_ASSIST_SUPER_SUB',
        'PLAYER_GOALS_TWO_OR_MORE_SUPER_SUB',
      ], market?.marketType?.marketCode)
      // Hockey markets without line
      || includes([
        'PLAYER_TOP_SHOTS',
        'PLAYER_TOP_GOALS',
        'PLAYER_TOP_SAVES',
        'PLAYER_TO_SCORE_AND_TEAM_TO_WIN',
        'PLAYER_GOALS_TWO_OR_MORE',
        'PLAYER_GOALS_THREE_OR_MORE',
      ], market?.marketType?.marketCode)
    )), (market) => {
      if (hasParamVariant(market?.marketType?.params)) {
        const paramVariantValue = getParamVariantValue(market?.marketType?.params);
        marketsPerPlayer[market?.marketType?.marketCode] = {
          ...marketsPerPlayer[market?.marketType?.marketCode] || {},
          [paramVariantValue]: {
            marketType: market?.marketType,
            params: market?.marketType?.params,
            selections: market?.selections,
            marketSummary: market?.marketSummary,
          },
          availableParamVariants: sortBy([
            ...(marketsPerPlayer[market?.marketType?.marketCode]?.availableParamVariants || []),
            paramVariantValue,
          ], (variant) => variant),
          isParamVariantMarket: true,
          marketCode: market?.marketType?.marketCode,
        };
      } else {
        marketsPerPlayer[market?.marketType?.marketCode] = {
          marketType: market?.marketType,
          params: market?.marketType?.params,
          selections: market?.selections,
          marketSummary: market?.marketSummary,
        };
      }
    });
    return {
      ...allPlayers,
      [playerIndex]: marketsPerPlayer,
    };
  },
  {},
);

const mergePlayerDataWithMarketPricesByTeam = (teamData, marketsByPlayerIndex, suspendedPlayersWithReasons = null, currentMarketUpdate) => reduce(
  teamData,
  (allPlayers, player) => {
    const playerMarketsWithPrices = marketsByPlayerIndex[player.playerIndex];
    const playerData = { ...player };
    if (playerMarketsWithPrices) {
      playerData.marketCodeDetails = map(playerData.marketCodeDetails, (market) => {
        let currentMarket = {};
        let simulatedMarket = {};
        if (currentMarketUpdate) {
          const mappedMarket = playerMarketsWithPrices[market.marketCode];
          const incomingParamVariants = reduce(mappedMarket, (allVariants, entry) => {
            const variantValue = getParamVariantValue(entry?.marketType?.params || entry?.params);
            return variantValue != null ? [...allVariants, variantValue] : allVariants;
          }, []);
          const isParamVariant = market.isParamVariantMarket
            || mappedMarket?.isParamVariantMarket
            || !!incomingParamVariants.length;

          if (isParamVariant) {
            const availableParamVariants = sortBy(uniq([
              ...(market.currentMarket?.availableParamVariants || []),
              ...incomingParamVariants,
            ]), (variant) => variant);
            currentMarket = {
              ...(market.currentMarket || {}),
              ...(mappedMarket || {}),
              availableParamVariants,
              isParamVariantMarket: true,
              marketCode: market.marketCode,
            };
          } else {
            currentMarket = mappedMarket || (market.currentMarket || {});
          }
          simulatedMarket = market.simulatedMarket || {};
        } else {
          simulatedMarket = playerMarketsWithPrices[market.marketCode] || (market.simulatedMarket || {});
          currentMarket = market.currentMarket || {};
        }
        return {
          ...market,
          simulatedMarket,
          currentMarket,
          isParamVariantMarket: currentMarket?.isParamVariantMarket || simulatedMarket?.isParamVariantMarket,
          availableParamVariants: sortBy(
            uniq([
              ...(currentMarket?.availableParamVariants || []),
              ...(simulatedMarket?.availableParamVariants || []),
            ]),
            (variant) => variant,
          ),
        };
      });
    }

    // Player suspended reason
    if (suspendedPlayersWithReasons !== null) {
      let playerSuspendedReason = '';
      const suspendedReasons = suspendedPlayersWithReasons[player.playerIndex];
      if (Array.isArray(suspendedReasons) && suspendedReasons.length) {
        const [suspendedReason] = suspendedReasons;
        playerSuspendedReason = suspendedReason;
      }
      playerData.suspendedReason = playerSuspendedReason;
    }

    return [
      ...allPlayers,
      ...[playerData],
    ];
  },
  [],
);

export const mergePlayerDataWithMarketPrices = ({
  playersTableData,
  marketsByPlayerIndex,
  suspendedPlayersWithReasons,
  comboPlayerMarkets,
  currentMarketUpdate = false,
}) => {
  const data = { ...playersTableData };

  if (marketsByPlayerIndex) {
    data.teamA = mergePlayerDataWithMarketPricesByTeam(data.teamA, marketsByPlayerIndex, suspendedPlayersWithReasons, currentMarketUpdate);
    data.teamB = mergePlayerDataWithMarketPricesByTeam(data.teamB, marketsByPlayerIndex, suspendedPlayersWithReasons, currentMarketUpdate);
  }

  if (comboPlayerMarkets) {
    data.playerComboMarketCodeDetails = reduce(
      data.playerComboMarketCodeDetails,
      (allMarkets, currentMarkets, key) => {
        const marketsWithouthPrices = currentMarkets;
        let marketsWithPrices = [];
        if (!currentMarketUpdate) {
          marketsWithPrices = find(comboPlayerMarkets, (comboMarket) => {
            const comboMarketKey = `(${comboMarket?.playersCombo?.first}, ${comboMarket?.playersCombo?.second})`;
            return comboMarketKey === key;
          })?.markets || [];
        } else {
          marketsWithPrices = filter(comboPlayerMarkets, (comboMarket) => {
            const comboMarketKey = `(${comboMarket?.marketType?.params?.FIRST_PLAYER_INDEX}, ${comboMarket?.marketType?.params?.SECOND_PLAYER_INDEX})`;
            return comboMarketKey === key;
          }) || [];
        }
        const mergedMarkets = map(marketsWithouthPrices, (market) => {
          const marketPrices = find(marketsWithPrices, { marketType: { marketCode: market.marketCode } });
          if (!marketPrices) return market;
          // Merge the market with the prices
          const newPrices = {
            marketType: marketPrices?.marketType,
            marketSummary: marketPrices?.marketSummary,
            selections: marketPrices?.selections,
            params: marketPrices?.marketType?.params,
          };
          return {
            ...market,
            simulatedMarket: currentMarketUpdate ? (market.simulatedMarket || {}) : newPrices,
            currentMarket: currentMarketUpdate ? newPrices : (market.currentMarket || {}),
          };
        });
        return {
          ...allMarkets,
          [key]: mergedMarkets,
        };
      }, {},
    );
  }

  return data;
};

export const getUpdatedMarketCodeDetails = (updatedMarketsByPlayerIndex, playerIndex, existingMarkets, sportId) => {
  const updatedMarkets = updatedMarketsByPlayerIndex[playerIndex];
  const updatedMarketCodeDetails = [];
  if (!updatedMarkets) return existingMarkets;
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(updatedMarkets)) {
    updatedMarketCodeDetails.push({
      ...(find(existingMarkets, { marketCode: key }) || {}),
      marketCode: key,
      marketName: marketMapper.playersMarketName(key, sportId),
      suspended: updatedMarkets && value === 'NOT_OFFERED',
      voided: updatedMarkets && value === 'VOIDED',
      excluded: updatedMarkets && value === 'EXCLUDED',
    });
  }
  return [
    ...updatedMarketCodeDetails,
    ...filter(existingMarkets, (market) => !includes(
      map(updatedMarketCodeDetails, (updatedMarket) => updatedMarket.marketCode), market.marketCode,
    )),
  ];
};

const updatePlayerPropsDataByTeam = (teamData, updatedPlayerProps, updatedMarkets, updatedIndexes, updatedData, sportId) => reduce(
  teamData,
  (allPlayers, player) => {
    const playerToUpdate = cloneDeep(player) || {};

    const updatedPlayer = find(updatedPlayerProps, { playerIndex: player.playerIndex });
    if (!updatedPlayer) {
      return [
        ...allPlayers,
        playerToUpdate,
      ];
    }
    const updatedParams = updatedPlayer || {};
    const updatedPlayerPositionIndex = updatedIndexes[player.playerIndex];

    playerToUpdate.marketCodeDetails = getUpdatedMarketCodeDetails(updatedMarkets, playerToUpdate.playerIndex, playerToUpdate.marketCodeDetails, sportId);
    const playerMarketsLength = playerToUpdate?.marketCodeDetails?.length;
    const suspendedMarketsLength = filter(playerToUpdate?.marketCodeDetails, (market) => market.suspended)?.length || 0;
    const lockedMarketsLength = filter(playerToUpdate?.marketCodeDetails, (market) => market.isLocked)?.length || 0;
    const voidedMarketsLength = filter(playerToUpdate?.marketCodeDetails, (market) => market.voided)?.length || 0;
    const excludedMarketsLength = filter(playerToUpdate?.marketCodeDetails, (market) => market.excluded)?.length || 0;
    const marketsByStatusLength = suspendedMarketsLength + lockedMarketsLength + voidedMarketsLength + excludedMarketsLength;
    playerToUpdate.isSuspended = playerMarketsLength && marketsByStatusLength === playerMarketsLength;
    const paramSource = get(updatedData?.playerParamSources, playerToUpdate.playerIndex) || playerToUpdate.paramSource;
    const calculatedParamsUpdatedAt = includes(updatedData?.updatedCalculatedPlayerParams, playerToUpdate.playerIndex) ? format(zonedTimeToUtc(updatedData?.timestamp, 'UTC'), 'hh:mm') : playerToUpdate.calculatedParamsUpdatedAt;
    return [
      ...allPlayers,
      {
        ...playerToUpdate,
        ...updatedParams,
        playerPositionIndex: updatedPlayerPositionIndex,
        paramSource,
        isPlayerInCalculatedMode: paramSource === 'CALCULATED',
        calculatedParamsUpdatedAt,
      },
    ];
  },
  [],
);

const addNewPlayerDataParamsManager = (existingDataByTeam, teamId, newData, squadData, updatedMarketsByPlayerIndex, updatedIndexes, sportId) => {
  const newPlayersToAdd = [];
  const existingPlayerIds = map(existingDataByTeam, (player) => player.playerIndex);

  forEach(newData, (player) => {
    if (!includes(existingPlayerIds, player.playerIndex)) {
      const squadPlayer = find(squadData, { playerIndex: player.playerIndex }) || {};
      const isCurrentTeamPlayer = squadPlayer?.teamId === teamId;
      if (!isCurrentTeamPlayer) return;
      const updatedMarketCodeDetails = getUpdatedMarketCodeDetails(updatedMarketsByPlayerIndex, player.playerIndex, player.marketCodeDetails, sportId);
      const isSuspended = updatedMarketCodeDetails.length
        && !filter(updatedMarketCodeDetails, (market) => !market.suspended)?.length;
      const updatedPlayerPositionIndex = updatedIndexes[player.playerIndex];

      newPlayersToAdd.push({
        ...player,
        playerName: squadPlayer?.personalInfo?.name,
        position: squadPlayer?.position || null,
        marketCodeDetails: updatedMarketCodeDetails,
        isSuspended,
        playerPositionIndex: updatedPlayerPositionIndex,
      });
    }
  });
  return newPlayersToAdd;
};

export const updatePlayerPropsData = (existingData, newData, squadData, competitionType, sportId) => {
  const dataToUpdate = cloneDeep(existingData);
  const updatedPlayerParams = cloneDeep(newData.playerParams);
  const updatedMarkets = cloneDeep(newData.playersMarketContext);
  const updatedPlayerPositionIndexes = cloneDeep(newData.playerPositionIndexes);
  dataToUpdate.teamA = updatePlayerPropsDataByTeam(
    dataToUpdate.teamA,
    updatedPlayerParams,
    updatedMarkets,
    updatedPlayerPositionIndexes,
    newData,
    sportId,
  );
  dataToUpdate.teamB = updatePlayerPropsDataByTeam(
    dataToUpdate.teamB,
    updatedPlayerParams,
    updatedMarkets,
    updatedPlayerPositionIndexes,
    newData,
    sportId,
  );
  const teamANewPlayersToAdd = addNewPlayerDataParamsManager(dataToUpdate.teamA, dataToUpdate?.teamAId, updatedPlayerParams, squadData, updatedMarkets, updatedPlayerPositionIndexes, sportId);
  const teamBNewPlayersToAdd = addNewPlayerDataParamsManager(dataToUpdate.teamB, dataToUpdate?.teamBId, updatedPlayerParams, squadData, updatedMarkets, updatedPlayerPositionIndexes, sportId);
  if (competitionType === 'NBA' || competitionType === 'FIBA' || sportId === SOCCER_ID) {
    dataToUpdate.playerComboMarketCodeDetails = reduce(
      newData.playersComboMarketContext,
      (allMarkets, marketUpdated, key) => {
        const currentMarketsState = dataToUpdate.playerComboMarketCodeDetails[key];
        const marketsMerged = map(marketUpdated, (_, updatedKey) => ({
          ...(find(currentMarketsState, ({ marketCode: updatedKey })) || {}),
          marketCode: updatedKey,
          isLocked: false,
          isComboMarket: true,
          marketName: formatPlayerName(marketMapper.comboPlayersMarketName(updatedKey)),
          suspended: marketUpdated[updatedKey] === 'NOT_OFFERED',
          voided: marketUpdated[updatedKey] === 'VOIDED',
          excluded: marketUpdated[updatedKey] === 'EXCLUDED',
        }));
        return {
          ...allMarkets,
          [key]: marketsMerged,
        };
      }, {},
    );
    // Check are players suspended based on the combo markets
    dataToUpdate.teamA = map(dataToUpdate.teamA, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(dataToUpdate.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          const markets = filter(dataToUpdate.playerComboMarketCodeDetails[key], (market) => !market.voided && !market.excluded);
          playerHasComboMarket = !!markets.length;
          return markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
        isComboMarket: true,
      };
    });
    dataToUpdate.teamB = map(dataToUpdate.teamB, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(dataToUpdate.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          const markets = filter(dataToUpdate.playerComboMarketCodeDetails[key], (market) => !market.voided && !market.excluded);
          playerHasComboMarket = !!markets.length;
          return markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
        isComboMarket: true,
      };
    });
  }

  return {
    ...dataToUpdate,
    teamA: [
      ...dataToUpdate.teamA,
      ...teamANewPlayersToAdd,
    ],
    teamB: [
      ...dataToUpdate.teamB,
      ...teamBNewPlayersToAdd,
    ],
  };
};

const mapPlayerSetupInferMarketDataByTeam = (eventData, teamPlayersData) => map(teamPlayersData,
  (player) => ({
    playerIndex: player.playerIndex,
    markets: map(player.markets, (market) => ({
      sportId: eventData.sportId,
      eventId: eventData.eventId,
      marketType: {
        marketCode: market.marketCode,
        params: market?.simulatedMarket?.params,
      },
      selections: market?.simulatedMarket?.selections,
      marketSummary: market?.simulatedMarket?.marketSummary,
    })),
  }));

export const mapPlayerSetupInferMarketData = (eventData, playerSetupTableData) => {
  let marketData = [];
  const teamAPlayerMarkets = map(playerSetupTableData.teamA, (player) => ({
    playerIndex: player.playerIndex,
    markets: player.marketCodeDetails,
  }));
  const teamBPlayerMarkets = map(playerSetupTableData.teamB, (player) => ({
    playerIndex: player.playerIndex,
    markets: player.marketCodeDetails,
  }));
  marketData = [
    ...mapPlayerSetupInferMarketDataByTeam(eventData, teamAPlayerMarkets),
    ...mapPlayerSetupInferMarketDataByTeam(eventData, teamBPlayerMarkets),
  ];
  return marketData;
};

export const mergePlayerParamsOnDemand = (playerSetupData, playerSetupTableData, updatedParams) => {
  const { isUSAView } = playerSetupData;
  const { homeTeam, awayTeam } = updatedParams;
  const existingData = cloneDeep(playerSetupTableData);

  const updatedData = {
    teamA: isUSAView ? awayTeam : homeTeam,
    teamB: isUSAView ? homeTeam : awayTeam,
  };
  const teamAData = map(updatedData.teamA?.playersParams, (player) => ({
    ...player?.playerParams,
    lineup: player?.lineup,
    voided: player?.voided,
    marketCodeDetails: player?.marketCodeDetails,
  }));
  const teamBData = map(updatedData.teamB?.playersParams, (player) => ({
    ...player?.playerParams,
    lineup: player?.lineup,
    voided: player?.voided,
    marketCodeDetails: player?.marketCodeDetails,
  }));
  const mergedData = {};

  // Update teamA data
  mergedData.teamA = map(existingData.teamA, (player) => ({
    ...find(teamAData, (updatedPlayer) => updatedPlayer.playerId === player.playerId),
    playerName: player.playerName,
    playerLocked: player.playerLocked,
    playerSet: player.playerSet,
    isSuspended: player.isSuspended,
    marketCodeDetails: player.marketCodeDetails,
    playerPositionIndex: player.playerPositionIndex,
  }));

  // Update teamB data
  mergedData.teamB = map(existingData.teamB, (player) => ({
    ...find(teamBData, (updatedPlayer) => updatedPlayer.playerId === player.playerId),
    playerName: player.playerName,
    playerLocked: player.playerLocked,
    playerSet: player.playerSet,
    isSuspended: player.isSuspended,
    marketCodeDetails: player.marketCodeDetails,
    playerPositionIndex: player.playerPositionIndex,
  }));

  return mergedData;
};

const mapInferResponsePlayerToTableRow = (player) => ({
  ...player?.playerParams,
  lineup: player?.lineup,
  voided: player?.voided,
  ...mapAdditionalPlayerParams(player),
});

const mergeBaseballInferTeamPlayers = (existingPlayers, updatedPlayers) => map(existingPlayers || [], (player) => {
  const updatedPlayer = find(updatedPlayers, { playerId: player.playerId }) || {};

  return {
    ...omit(updatedPlayer, ['marketCodeDetails']),
    playerName: player.playerName,
    playerLocked: player.playerLocked,
    playerSet: player.playerSet,
    isSuspended: player.isSuspended,
    marketCodeDetails: player.marketCodeDetails,
  };
});

export const mergeBaseballPlayerParamsOnDemand = (playerSetupData, playerSetupTableData, updatedParams) => {
  const { isUSAView } = playerSetupData;
  const { homeTeam, awayTeam } = updatedParams;
  const existingData = cloneDeep(playerSetupTableData) || { teamA: [], teamB: [] };

  const updatedTeams = {
    teamA: isUSAView ? awayTeam : homeTeam,
    teamB: isUSAView ? homeTeam : awayTeam,
  };

  const teamAUpdates = map(updatedTeams.teamA?.playersParams, mapInferResponsePlayerToTableRow);
  const teamBUpdates = map(updatedTeams.teamB?.playersParams, mapInferResponsePlayerToTableRow);

  return {
    teamA: mergeBaseballInferTeamPlayers(existingData.teamA, teamAUpdates),
    teamB: mergeBaseballInferTeamPlayers(existingData.teamB, teamBUpdates),
  };
};

export const extractUpdatedPlayerSetupInferData = (initialData, newData) => {
  const updatedData = {};
  updatedData.teamA = filter(newData.teamA,
    (player) => !isEqual(
      find(initialData.teamA, { playerId: player.playerId }),
      player,
    ));
  updatedData.teamB = filter(newData.teamB,
    (player) => !isEqual(
      find(initialData.teamB, { playerId: player.playerId }),
      player,
    ));
  return updatedData;
};

// Trading UI

const updatePlayerPropsDataByTeamTradingUI = (teamData, updatedPlayerProps, updatedMarketsByPlayerIndex, updatedIndexes, updatedData, sportId) => reduce(
  teamData,
  (allPlayers, player) => {
    const playerToUpdate = cloneDeep(player) || {};

    const updatedPlayer = find(updatedPlayerProps, { playerIndex: player.playerIndex }) || {};
    const updatedParams = updatedPlayer || {};
    const updatedMarketCodeDetails = getUpdatedMarketCodeDetails(updatedMarketsByPlayerIndex, updatedPlayer.playerIndex, playerToUpdate.marketCodeDetails, sportId);
    const isSuspended = updatedMarketCodeDetails.length
        && !filter(updatedMarketCodeDetails, (market) => !market.suspended)?.length;
    const updatedPlayerPositionIndex = updatedIndexes[player.playerIndex];
    const paramSource = get(updatedData?.playerParamSources, playerToUpdate.playerIndex) || playerToUpdate.paramSource;

    return [
      ...allPlayers,
      {
        ...playerToUpdate,
        ...updatedParams,
        marketCodeDetails: updatedMarketCodeDetails,
        isSuspended,
        playerPositionIndex: updatedPlayerPositionIndex,
        isPlayerInCalculatedMode: paramSource === 'CALCULATED',
      },
    ];
  },
  [],
);

const addNewPlayerDataTradingUI = (existingData, newData, squadData, updatedMarketsByPlayerIndex, updatedIndexes, sportId) => {
  const newTeamAPlayers = [];
  const newTeamBPlayers = [];
  const existingPlayerIds = [
    ...map(existingData.teamA?.playerProps, (player) => player.playerIndex),
    ...map(existingData.teamB?.playerProps, (player) => player.playerIndex),
  ];

  forEach(newData, (player) => {
    if (!includes(existingPlayerIds, player.playerIndex)) {
      const squadPlayer = find(squadData, { playerIndex: player.playerIndex });
      const isTeamAPlayer = squadPlayer?.teamId === existingData.teamA?.teamId;
      const updatedMarketCodeDetails = getUpdatedMarketCodeDetails(updatedMarketsByPlayerIndex, player.playerIndex, player.marketCodeDetails, sportId);
      const isSuspended = updatedMarketCodeDetails.length
        && !filter(updatedMarketCodeDetails, (market) => !market.suspended)?.length;
      const updatedPlayerPositionIndex = updatedIndexes[player.playerIndex];
      if (isTeamAPlayer) {
        newTeamAPlayers.push({
          ...player,
          playerName: squadPlayer?.personalInfo?.name,
          position: squadPlayer?.position || null,
          marketCodeDetails: updatedMarketCodeDetails,
          isSuspended,
          playerPositionIndex: updatedPlayerPositionIndex,
        });
      } else {
        newTeamBPlayers.push({
          ...player,
          playerName: squadPlayer?.personalInfo?.name,
          position: squadPlayer?.position || null,
          marketCodeDetails: updatedMarketCodeDetails,
          isSuspended,
          playerPositionIndex: updatedPlayerPositionIndex,
        });
      }
    }
  });
  return {
    newTeamAPlayers,
    newTeamBPlayers,
  };
};

export const updatePlayerPropsDataTradingUI = (existingData, newData, squadData, competitionType, sportId) => {
  const dataToUpdate = cloneDeep(existingData);
  const updatedPlayerParams = cloneDeep(newData.playerParams);
  const updatedMarketsByPlayerIndex = cloneDeep(newData.playersMarketContext);
  const updatedPlayerPositionIndexes = cloneDeep(newData.playerPositionIndexes);

  dataToUpdate.teamA.playerProps = updatePlayerPropsDataByTeamTradingUI(
    dataToUpdate.teamA.playerProps,
    updatedPlayerParams,
    updatedMarketsByPlayerIndex,
    updatedPlayerPositionIndexes,
    newData,
    sportId,
  );
  dataToUpdate.teamB.playerProps = updatePlayerPropsDataByTeamTradingUI(
    dataToUpdate.teamB.playerProps,
    updatedPlayerParams,
    updatedMarketsByPlayerIndex,
    updatedPlayerPositionIndexes,
    newData,
    sportId,
  );

  // Adding newly added players
  const { newTeamAPlayers, newTeamBPlayers } = addNewPlayerDataTradingUI(existingData, updatedPlayerParams, squadData, updatedMarketsByPlayerIndex, updatedPlayerPositionIndexes, sportId);
  dataToUpdate.teamA.playerProps = [
    ...dataToUpdate.teamA.playerProps,
    ...newTeamAPlayers,
  ];

  dataToUpdate.teamB.playerProps = [
    ...dataToUpdate.teamB.playerProps,
    ...newTeamBPlayers,
  ];
  if (competitionType === 'NBA' || competitionType === 'FIBA' || sportId === SOCCER_ID) {
    dataToUpdate.playerComboMarketCodeDetails = reduce(
      newData.playersComboMarketContext,
      (allMarkets, marketUpdated, key) => {
        // Extract the other player index from the key
        const indices = key.match(/\((-?\d+),\s*(-?\d+)\)/);
        // eslint-disable-next-line no-unused-vars
        const [_, index1, index2] = indices;
        const currentMarketsState = dataToUpdate.playerComboMarketCodeDetails[key];
        const marketsMerged = map(marketUpdated, (__, updatedKey) => ({
          ...(find(currentMarketsState, ({ marketCode: updatedKey })) || {}),
          marketCode: updatedKey,
          isLocked: false,
          isComboMarket: true,
          marketName: formatPlayerName(marketMapper.comboPlayersMarketName(updatedKey)),
          suspended: marketUpdated[updatedKey] === 'NOT_OFFERED',
          voided: marketUpdated[updatedKey] === 'VOIDED',
          excluded: marketUpdated[updatedKey] === 'EXCLUDED',
          playersCombo: {
            first: parseInt(index1, 10),
            second: parseInt(index2, 10),
          },
        }));
        return {
          ...allMarkets,
          [key]: marketsMerged,
        };
      }, {},
    );
    // Check are players suspended based on the combo markets
    dataToUpdate.teamA.playerProps = map(dataToUpdate.teamA.playerProps, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(dataToUpdate.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          const markets = filter(dataToUpdate.playerComboMarketCodeDetails[key], (market) => !market.voided && !market.excluded);
          playerHasComboMarket = !!markets.length;
          return markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
        isComboMarket: true,
      };
    });
    dataToUpdate.teamB.playerProps = map(dataToUpdate.teamB.playerProps, (player) => {
      let playerHasComboMarket = false;
      const isSuspendedInComboMarkets = Object.keys(dataToUpdate.playerComboMarketCodeDetails).every((key) => {
        if (key.includes(player.playerIndex.toString())) {
          const markets = filter(dataToUpdate.playerComboMarketCodeDetails[key], (market) => !market.voided && !market.excluded);
          playerHasComboMarket = !!markets.length;
          return markets.every((market) => market.suspended);
        }
        return true;
      });
      return {
        ...player,
        isSuspended: playerHasComboMarket ? isSuspendedInComboMarkets && player.isSuspended : player.isSuspended,
        isComboMarket: true,
      };
    });
  }

  return dataToUpdate;
};
const mapTeamLineupPlayerParams = (players) => map(players, (player) => ({
  playerParams: {
    playerId: player.playerId,
    playerIndex: player.playerIndex,
    sport: player.sport,
    pitcherParams: player.pitcherParams,
    hitterParams: player.hitterParams,
  },
  marketCodeDetails: map(player.marketCodeDetails, (market) => ({ marketCode: market.marketCode, suspended: market.suspended })),
  lineup: player.lineup,
  playerPositionIndex: player.playerPositionIndex,
}));
export const mapPlayerSetupBaseballTeamLineupData = (data) => {
  const pitchers = filter(data, (player) => player.isPitcher);
  const hitters = filter(data, (player) => player.isHitter);
  return {
    pitchers: mapTeamLineupPlayerParams(pitchers),
    hitters: mapTeamLineupPlayerParams(hitters),
  };
};

export const getPlayerMinutesRemainingValue = ({
  probabilities,
  playerIndex,
  teamSide,
}) => {
  if (!probabilities || !playerIndex || !teamSide) return 0;
  const values = teamSide === 'HOME' ? probabilities.homePlayersRemainingMinutes : probabilities.awayPlayersRemainingMinutes;
  return values && (values[playerIndex] ? parseFloat((values[playerIndex]).toFixed(2)) : 0);
};

export const getCurrentHoldPointPercentage = ({
  probabilities,
  teamSide,
}) => {
  if (!probabilities || !teamSide) {
    return {
      playerA: 0,
      playerB: 0,
    };
  }
  const values = teamSide === 'HOME' ? probabilities.homeCurrentHoldPointPercentage : probabilities.awayCurrentHoldPointPercentage;
  const playerAValue = values && (values.PLAYER_A ? parseFloat((values.PLAYER_A).toFixed(2)) : 0);
  const playerBValue = values && (values.PLAYER_B ? parseFloat((values.PLAYER_B).toFixed(2)) : 0);
  return {
    playerA: playerAValue,
    playerB: playerBValue,
  };
};

export const mapMarketsByPlayerIndexOnSocketUpdate = ({
  incomingMarkets,
  availableMarkets,
  availableComboMarkets,
}) => {
  const incomingMarketsMainLine = filter(incomingMarkets, (market) => (has(market.marketType.params, 'LINE') ? market.marketSummary.isMainLine : true));
  const availablePlayerMarkets = map(availableMarkets, (market) => market.marketCode);
  const availableComboMarketsCodes = map(availableComboMarkets, (market) => market.marketCode);
  const playerMarketsToUpdate = filter(incomingMarketsMainLine, (market) => includes(availablePlayerMarkets, market?.marketType.marketCode));
  const comboPlayerMarketsToUpdate = filter(incomingMarketsMainLine, (market) => includes(availableComboMarketsCodes, market?.marketType.marketCode));
  let marketsByPlayerIndex;
  if (playerMarketsToUpdate.length) {
    marketsByPlayerIndex = reduce(
      playerMarketsToUpdate,
      (result, market) => {
        const marketsMapped = cloneDeep(result);
        if (hasParamVariant(market?.marketType?.params)) {
          const paramVariantValue = getParamVariantValue(market?.marketType?.params);
          marketsMapped[market?.marketType?.params?.PLAYER_INDEX] = marketsMapped[market?.marketType?.params?.PLAYER_INDEX] || {};
          marketsMapped[market?.marketType?.params?.PLAYER_INDEX][market.marketType.marketCode] = marketsMapped[market?.marketType?.params?.PLAYER_INDEX][market.marketType.marketCode] || {};
          marketsMapped[market?.marketType?.params?.PLAYER_INDEX][market.marketType.marketCode][paramVariantValue] = market;
        } else {
          marketsMapped[market?.marketType?.params?.PLAYER_INDEX] = marketsMapped[market?.marketType?.params?.PLAYER_INDEX] || {};
          marketsMapped[market?.marketType?.params?.PLAYER_INDEX][market.marketType.marketCode] = market;
        }
        return marketsMapped;
      },
      {},
    );
  } else {
    marketsByPlayerIndex = null;
  }

  return {
    marketsByPlayerIndex,
    comboPlayerMarketsToUpdate,
  };
};

const mapSubmittedPlayerMarkets = (markets = []) => map(markets, (market) => ({
  ...omit(market, ['marketNotSubmitted']),
  isLocked: false,
}));

const mapSubmittedPlayersByTeam = (players = []) => map(players, (player) => ({
  ...player,
  playerLocked: true,
  marketCodeDetails: mapSubmittedPlayerMarkets(player.marketCodeDetails),
}));

// Clears client-only pending market flags and locks submitted players after a successful submit.
export const mapPlayerSetupTableDataAfterSubmit = (tableData) => {
  if (!tableData) return tableData;

  const syncedData = {
    ...tableData,
    teamA: mapSubmittedPlayersByTeam(tableData.teamA),
    teamB: mapSubmittedPlayersByTeam(tableData.teamB),
  };

  if (tableData.playerComboMarketCodeDetails) {
    syncedData.playerComboMarketCodeDetails = reduce(
      tableData.playerComboMarketCodeDetails,
      (allMarkets, markets, key) => ({
        ...allMarkets,
        [key]: mapSubmittedPlayerMarkets(markets),
      }),
      {},
    );
  }

  return syncedData;
};
